import { z } from "zod";
import {
  MAX_LOSS_PCT4,
  MAX_STOCK_QTY_Q4,
  MAX_STRUCTURE_ITEMS,
  isIsoDate,
  type BaseState,
  type StatusTone,
} from "@cx/domain";
import { listQuery } from "./common";
import { blank, text } from "./fields";
import { ulidParam } from "./ids";
import { lotInput } from "./lots";
import { businessDate } from "./prices";

// ===== Estrutura de produção e ordens de produção (RF-SUP-05, T3.2) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const optDate = blank.pipe(businessDate.nullable());
const qtyQ4 = z
  .number("Informe a quantidade")
  .int("Quantidade inválida")
  .min(1, "A quantidade deve ser maior que zero")
  .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite");
const lossPct4 = z
  .number("Informe a perda")
  .int("Percentual inválido")
  .min(0, "A perda vai de 0% a 99,9999%")
  .max(MAX_LOSS_PCT4, "A perda vai de 0% a 99,9999%")
  .default(0);

export const structureItemInput = z.object({ componentId: ulidParam, qtyQ4, lossPct4 });
/** Estrutura (insumos por 1 unidade do produto). Lista vazia apaga a estrutura. */
export const productStructureInput = z.object({
  items: z
    .array(structureItemInput, "Informe os insumos")
    .max(MAX_STRUCTURE_ITEMS, `No máximo ${MAX_STRUCTURE_ITEMS} insumos`),
});
export type ProductStructureInput = z.output<typeof productStructureInput>;

export type StructureItemView = {
  componentId: string;
  name: string;
  sku: string | null;
  unit: string;
  qtyQ4: number;
  lossPct4: number;
  stockControl: boolean;
  /** Custo médio do insumo (só com products:read_cost). */
  avgCostCents?: number;
};
export type ProductStructureView = { productId: string; items: StructureItemView[] };

/** Insumo da OP (cópia editável da estrutura enquanto não há apontamento). */
export const productionItemInput = z.object({ componentId: ulidParam, qtyPerUnitQ4: qtyQ4, lossPct4 });

export const productionOrderInput = z
  .object({
    productId: ulidParam,
    quantityQ4: qtyQ4,
    /** Depósito de onde saem os insumos e onde entra o acabado; nulo = padrão da empresa. */
    sourceWarehouseId: nullish(ulidParam),
    targetWarehouseId: nullish(ulidParam),
    responsible: text(100),
    plannedStart: optDate,
    plannedEnd: optDate,
    notes: text(2000),
    /** Lote do acabado (padrão dos apontamentos; obrigatório para produto com controle de lotes ao apontar). */
    lot: nullish(lotInput),
    /** Insumos; nulo = copia a estrutura do produto. */
    items: nullish(
      z
        .array(productionItemInput)
        .min(1, "Inclua ao menos um insumo")
        .max(MAX_STRUCTURE_ITEMS, `No máximo ${MAX_STRUCTURE_ITEMS} insumos`),
    ),
  })
  .superRefine((v, ctx) => {
    if (v.plannedStart && v.plannedEnd && isIsoDate(v.plannedStart) && isIsoDate(v.plannedEnd))
      if (v.plannedEnd < v.plannedStart)
        ctx.addIssue({
          code: "custom",
          path: ["plannedEnd"],
          message: "A previsão de término deve ser igual ou posterior ao início",
        });
  });
export type ProductionOrderInput = z.output<typeof productionOrderInput>;
export type ProductionOrderFormValues = z.input<typeof productionOrderInput>;

export const productionOrderListQuery = listQuery.extend({
  statusId: ulidParam.optional(),
  productId: ulidParam.optional(),
});
export type ProductionOrderListQuery = z.output<typeof productionOrderListQuery>;

export const productionStatusInput = z.object({ toStatusId: ulidParam, expectedFromStatusId: ulidParam });
export type ProductionStatusInput = z.output<typeof productionStatusInput>;

/** Apontamento (produção parcial ou total). Lote nulo = lote da OP. */
export const productionRunInput = z.object({
  qtyQ4,
  date: businessDate.optional(),
  lot: nullish(lotInput),
  notes: text(500),
});
export type ProductionRunInput = z.output<typeof productionRunInput>;

/** Finalizar: produzir o saldo (apontamento do que falta) ou encerrar com o que já foi produzido. */
export const productionFinishInput = z.object({
  mode: z.enum(["produce_remaining", "close"], "Escolha como finalizar"),
  date: businessDate.optional(),
  lot: nullish(lotInput),
});
export type ProductionFinishInput = z.output<typeof productionFinishInput>;

export const productionRunReverseInput = z.object({ reason: text(500) });
export type ProductionRunReverseInput = z.output<typeof productionRunReverseInput>;

export type ProductionStatusRef = { id: string; label: string; color: StatusTone; baseState: BaseState };

export type ProductionItemView = {
  id: string;
  position: number;
  componentId: string;
  name: string;
  sku: string | null;
  unit: string;
  qtyPerUnitQ4: number;
  lossPct4: number;
  requiredQ4: number;
  consumedQ4: number;
  stockControl: boolean;
  /** Disponível no depósito de origem. */
  availableQ4: number | null;
};

export type ProductionRunView = {
  id: string;
  number: number;
  date: string;
  qtyQ4: number;
  source: "manual" | "finish" | "status";
  lot: { id: string; code: string; expiresAt: string | null } | null;
  /** Custo dos insumos (só com products:read_cost). */
  costCents?: number;
  unitCostCents?: number;
  items: { componentId: string; name: string; qtyQ4: number; costCents?: number }[];
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  reversedAt: string | null;
  reverseReason: string | null;
};

type Ref = { id: string; name: string };

export type ProductionOrderView = {
  id: string;
  number: number;
  product: { id: string; name: string; sku: string | null; unit: string; batchControl: boolean };
  quantityQ4: number;
  producedQ4: number;
  closedQ4: number;
  remainingQ4: number;
  progress: "none" | "partial" | "complete";
  sourceWarehouse: Ref;
  targetWarehouse: Ref;
  responsible: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  notes: string | null;
  lot: { code: string; manufacturedAt: string | null; expiresAt: string | null } | null;
  items: ProductionItemView[];
  runs: ProductionRunView[];
  /** Custo apurado dos apontamentos ativos e unitário médio (só com products:read_cost). */
  producedCostCents?: number;
  unitCostCents?: number;
  status: ProductionStatusRef;
  nextStatuses: ProductionStatusRef[];
  lockedReason: string | null;
  canProduce: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ProductionOrderListItem = {
  id: string;
  number: number;
  productName: string;
  productSku: string | null;
  unit: string;
  quantityQ4: number;
  producedQ4: number;
  plannedEnd: string | null;
  responsible: string | null;
  status: ProductionStatusRef;
};

export type ProductionOrderCreated = { id: string; number: number };
export type ProductionRunResult = { runId: string; number: number; movements: number };
export type ProductionTransitionResult = {
  changed: boolean;
  fromStatusId: string;
  toStatusId: string;
  produced: boolean;
  reversed: number;
};
